import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { defaultErrorResponses } from './errors';
import { AutoFullScanEligibilitySchema } from '../schemas/subscription';

const c = initContract();

/**
 * Subscription Management Contract
 *
 * Manages freemium model with email count limits:
 * - Free tier: 500 emails max
 * - Pro tier: Unlimited emails
 *
 * Subscription status is derived from Stripe synced data
 * (via Supabase Stripe Sync Engine).
 */

// Subscription status and tier types
const SubscriptionTier = z.enum(['free', 'pro']);
const SubscriptionStatus = z.enum(['active', 'canceled', 'past_due', 'expired']);

/**
 * GET /v1/subscription/status - Get current subscription and usage
 */
export const GetSubscriptionStatusResponseSchema = z.object({
  tier: SubscriptionTier,
  status: SubscriptionStatus,
  emails_scanned_total: z.number().int().nonnegative(),
  emails_limit: z.number().int().nonnegative().nullable(),      // null = unlimited
  emails_remaining: z.number().int().nonnegative().nullable(),  // null = unlimited
  expires_at: z.string().datetime().nullable(),
  upgrade_url: z.string().url(),
  manage_url: z.string().url().nullable(),
  // True when a Pro user has not yet completed their first unlimited
  // (non-free-sample) full scan - i.e. the post-upgrade full scan is still
  // owed. Always false for free-tier users. Drives the automatic
  // post-upgrade full scan on the frontend (see use-scan-status-controller).
  needs_full_scan: z.boolean(),
  auto_full_scan_eligibility: AutoFullScanEligibilitySchema.optional(),
});

export type GetSubscriptionStatusResponse = z.infer<typeof GetSubscriptionStatusResponseSchema>

/**
 * POST /v1/subscription/checkout - Create checkout session
 *
 * Returns URL to redirect user to external payment provider
 */
export const CreateCheckoutRequestSchema = z.object({
  plan: z.enum(['premium_annual', 'premium_monthly']),
  success_url: z.string().url(),
  cancel_url: z.string().url(),
})

export type CreateCheckoutRequest = z.infer<typeof CreateCheckoutRequestSchema>

export const CreateCheckoutResponseSchema = z.object({
  checkout_url: z.string().url(),
})

export type CreateCheckoutResponse = z.infer<typeof CreateCheckoutResponseSchema>

export const subscriptionContract = c.router({
  /**
   * GET /v1/subscription/status - Get subscription status and usage
   */
  getStatus: {
    method: 'GET',
    path: '/v1/subscription/status',
    responses: {
      200: GetSubscriptionStatusResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Get subscription status',
      description: 'Returns current tier, status, and email usage limits',
    },
  },

  /**
   * POST /v1/subscription/checkout - Create checkout session
   */
  createCheckout: {
    method: 'POST',
    path: '/v1/subscription/checkout',
    body: CreateCheckoutRequestSchema,
    responses: {
      200: CreateCheckoutResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Create checkout session',
      description: 'Initiates subscription checkout and returns provider URL',
    },
  },
})
